import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, computed, signal } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { IconComponent } from '../../../../../../@core/components/ui/icon/icon.component';

import { BobAssistedHoursData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

const SURFACE_COLORS = ['#FF7B5C', '#FFB347', '#7C6FFF', '#C3A0FF', '#4ECDC4'];
// const RING_RADII = [92, 74, 56, 38, 20];
const RING_WIDTHS = [18, 16, 14, 12, 10];

const OUTERMOST_RADIUS = 99;
const GAP = 6;

const RING_RADII = RING_WIDTHS.reduce<number[]>((acc, width, i) => {
    if (i === 0) {
        acc.push(OUTERMOST_RADIUS);
    } else {
        const prevRadius = acc[i - 1];
        const prevWidth = RING_WIDTHS[i - 1];

        acc.push(
            prevRadius -
            (prevWidth / 2 + width / 2 + GAP)
        );
    }
    return acc;
}, []);

/**
 * Bob Assisted Hours Widget
 *
 * Total hours Bob spent assisting per surface, rendered as a bar list +
 * echarts donut. Reads state from `WorkspaceOverviewStateService`.
 *
 * Tooltip: "Displays the total duration of assistance provided by Bob
 * across all your sessions".
 */
@Component({
    selector: 'app-bob-assisted-hours-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule, NgxEchartsModule, IconComponent, SkeletonModule],
    providers: [
        { provide: NGX_ECHARTS_CONFIG, useValue: { echarts: () => import('echarts') } },
    ],
    templateUrl: './bob-assisted-hours-widget.component.html',
    styleUrl: './bob-assisted-hours-widget.component.scss',
})
export class BobAssistedHoursWidgetComponent implements OnInit {
    readonly data = signal<BobAssistedHoursData | null>(null);
    readonly loading = signal(false);
    readonly colors = SURFACE_COLORS;

    readonly chartOptions = computed<EChartsOption>(() => {
        const surfaces = this.data()?.surfaces ?? [];

        const buildRing = (color: string, width: number, radius: number, value: number, name: string) => ({
            type: 'gauge' as const,
            name,
            radius: `${radius}%`,
            startAngle: 90,
            endAngle: -270,
            min: 0,
            max: 100,
            pointer: { show: false },
            progress: {
                show: true,
                roundCap: true,
                clip: false,
                width,
                itemStyle: { color: color + '99' },
            },
            axisLine: {
                roundCap: true,
                lineStyle: { width, color: [[1, color + '1F']] as [[number, string]] },
            },
            splitLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            detail: { show: false },
            data: [{ value }],
        });

        const series = surfaces.length
            ? surfaces.map((s, i) =>
                buildRing(
                    SURFACE_COLORS[i % SURFACE_COLORS.length],
                    RING_WIDTHS[i] ?? 12,
                    RING_RADII[i] ?? Math.max(18 - (i - 4) * 18, 8),
                    s.percent,
                    s.name,
                ))
            : [buildRing('#E5E7EB', 26, 92, 0, '')];

        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    if (!params.seriesName) return '';
                    const surface = surfaces.find(s => s.name === params.seriesName);
                    const hours = surface ? this.formatHours(surface.hours) : params.value;
                    return `${params.seriesName}: ${hours} hrs`;
                },
            },
            series,
        };
    });

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            this.fetch(this.state.viewMode(), this.state.period(), this.state.effectiveRange(), this.state.workspaceId());
        });
    }

    ngOnInit(): void { /* initial fetch triggered by the effect above */ }

    formatHours(value: number): string {
        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
        if (value % 1 !== 0) return value.toFixed(1);
        return String(value);
    }

    private fetch(
        mode: ReturnType<WorkspaceOverviewStateService['viewMode']>,
        period: ReturnType<WorkspaceOverviewStateService['period']>,
        range: ReturnType<WorkspaceOverviewStateService['effectiveRange']>,
        workspaceId: string,
    ): void {
        this.loading.set(true);
        this.service.getBobAssistedHours({ mode, period, range, workspaceId }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: () => this.loading.set(false),
        });
    }
}
